import { ReactNode } from "react";

import acceptReviewSvg from "@/../../hypetrain/static/img/accept-review.svg";
import joinSessionSvg from "@/../../hypetrain/static/img/join-session.svg";
import serviceCompleteSvg from "@/../../hypetrain/static/img/service-complete.svg";
import sessionCanceledSvg from "@/../../hypetrain/static/img/session-canceled.svg";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { Box, Flex } from "@/components/layout/flexbox";
import { FlexibleButton } from "@/components/pieces/button/FlexibleButton";
import { CoachAcceptButtons } from "@/components/pieces/coaching/reviews/CoachAcceptButtons";
import { MarkCompleteButton } from "@/components/pieces/coaching/reviews/review-sidebar/MarkCompleteButton";
import { MarkRevisedButton } from "@/components/pieces/coaching/reviews/review-sidebar/MarkRevisedButton";
import {
  getReviewAction,
  getRoundState,
} from "@/components/pieces/coaching/reviews/utils";
import { LinkButton } from "@/components/pieces/link/LinkButton";
import { CARD_CLASSES, SUBTEXT_COLOR } from "@/css/constants";
import { FullReview, ReviewRoundState } from "@/types/meetings";
import { ensureNonNullArray } from "@/utils/array";

interface ReviewCardProps {
  icon: string;
  title: ReactNode;
  subtitle: ReactNode;
  actionButton?: ReactNode;
  children?: ReactNode;
}

function ReviewCard({
  icon,
  title,
  subtitle,
  actionButton,
  children,
}: ReviewCardProps) {
  return (
    <Flex
      mb={24}
      p={24}
      backgroundColor="#FFF"
      className={CARD_CLASSES}
      justifyContent="space-between"
      flexWrap="wrap"
      alignItems="center"
    >
      <Flex alignItems="center">
        <EmbedSvg src={icon} width={64} />
        <Flex
          ml={3}
          flex={1}
          flexDirection={["column", "column", "column", "row"]}
          flexWrap={"wrap"}
        >
          <Box flex={1} minWidth={250} mr={2}>
            <Box fontWeight={500} fontSize={20}>
              {title}
            </Box>
            <Box color={SUBTEXT_COLOR}>{subtitle}</Box>
          </Box>

          {actionButton && (
            <Box width={["100%", "100%", "100%", "auto"]} minWidth={180} mt={2}>
              {actionButton}
            </Box>
          )}
        </Flex>
      </Flex>
      {children}
    </Flex>
  );
}

interface ReviewViewProps {
  type: string;
  review: FullReview;
  isCoach: boolean;
}

// Pure component for different review views
function ReviewView({ type, review, isCoach }: ReviewViewProps) {
  const bookReviewButton = (
    <FlexibleButton
      as={LinkButton}
      to="/schedule"
      style={{
        padding: "12px 24px",
      }}
    >
      Book Review
    </FlexibleButton>
  );

  switch (type) {
    case "COACH_ACCEPT":
      return (
        <ReviewCard
          icon={joinSessionSvg}
          title={
            isCoach
              ? "Accept this service request?"
              : "Your request has been sent"
          }
          subtitle={getReviewAction(review.state, isCoach, review.nextDate)}
          actionButton={
            isCoach ? <CoachAcceptButtons id={review.id} large={true} /> : null
          }
        />
      );

    case "COMPLETE_ROUND": {
      const isFinalCheck =
        review.product?.includesFinalCheck &&
        review.numRounds === review.numRoundsComplete;
      return (
        <ReviewCard
          icon={isCoach ? joinSessionSvg : acceptReviewSvg}
          title={
            isCoach
              ? `Please complete the ${isFinalCheck ? "final check" : "request"}`
              : `Waiting on coach to complete your ${isFinalCheck ? "final check" : "request"}`
          }
          subtitle={
            isCoach
              ? "Please use the chat to send any links, videos, or documents"
              : "They will send follow-up material via the chat"
          }
          actionButton={isCoach ? <MarkCompleteButton id={review.id} /> : null}
        />
      );
    }

    case "COMPLETE_REVISION":
      return (
        <ReviewCard
          icon={isCoach ? acceptReviewSvg : joinSessionSvg}
          title={
            isCoach
              ? "Waiting on client to return revisions"
              : `You're up! Please send your revisions.`
          }
          subtitle={
            isCoach
              ? "They will send follow-up material via the chat"
              : `Make the suggested changes and send it back in the chat.`
          }
          actionButton={isCoach ? null : <MarkRevisedButton id={review.id} />}
        />
      );

    case "REVIEW_COMPLETE":
      return (
        <ReviewCard
          icon={serviceCompleteSvg}
          title="All done!"
          subtitle="The journey of a thousand miles begins with one step"
          actionButton={isCoach ? null : bookReviewButton}
        />
      );

    case "REVIEW_CANCELED":
      return (
        <ReviewCard
          icon={sessionCanceledSvg}
          title="This review is canceled"
          subtitle="Every day is a new beginning. Take a deep breath and start again."
          actionButton={isCoach ? null : bookReviewButton}
        />
      );

    default:
      return null;
  }
}

interface ReviewActionsProps {
  review: FullReview;
  isCoach: boolean;
}

// Main component with simplified logic
export function ReviewActions({ review, isCoach }: ReviewActionsProps) {
  // Determine the current state of the review
  const getReviewState = () => {
    // Check if waiting on coach acceptance
    if (!review.acceptedAt && !review.completedAt && !review.canceledAt) {
      return "COACH_ACCEPT";
    }

    const rounds = ensureNonNullArray(review.rounds);

    // Check review rounds state
    const roundStates = rounds.reduce<
      Partial<Record<ReviewRoundState, boolean>>
    >((agg, _, i) => {
      const roundState = getRoundState(review, rounds, i + 1);
      if (roundState === "AWAITING_REVISION") {
        agg["AWAITING_REVISION"] = true;
      } else if (roundState === "IN_PROGRESS") {
        agg["IN_PROGRESS"] = true;
      }
      return agg;
    }, {});

    // Check for final check state
    const waitingOnFinalCheck =
      review.product?.includesFinalCheck &&
      review.numRounds === review.numRoundsComplete &&
      !review.completedAt;

    // Determine view to render based on state
    if (roundStates["IN_PROGRESS"]) {
      return "COMPLETE_ROUND";
    } else if (roundStates["AWAITING_REVISION"]) {
      return "COMPLETE_REVISION";
    } else if (waitingOnFinalCheck) {
      return "COMPLETE_ROUND"; // Using the same component for final check
    } else if (review.canceledAt) {
      return "REVIEW_CANCELED";
    } else if (review.completedAt) {
      return "REVIEW_COMPLETE";
    }

    return null;
  };

  const currentState = getReviewState();

  if (!currentState) {
    return null;
  }

  return <ReviewView type={currentState} review={review} isCoach={isCoach} />;
}
